'use client'

import {ChevronDown, ChevronRight} from 'lucide-react'
import {useEffect, useMemo} from 'react'
import {cn, tv} from 'tailwind-variants'

import {useLocation, useNavigate} from 'react-router-dom'
import {Collapsible, CollapsibleContent} from 'src/components/collapsible'
import {HoverCard, HoverCardContent, HoverCardTrigger} from 'src/components/hover-card'
import {SidebarNavLink} from 'src/components/portal-migration/sidebar-nav-link'
import useWindowSize from 'src/components/slider/hooks/useWindowSize'
import {NavigationMenuType} from 'src/layouts/main-layout/app-sidebar/navigation/types/navigation-menu.type'
import {isPathActive} from 'src/layouts/main-layout/app-sidebar/utils/is-path-active.helper'
import {isMigratedLegacyPath, isPortalRouteHybridEnabled, navigateToMigratedRouteOnNewPortal} from 'src/lib/portal-migration'
import {isNewPortalEnabledForPath} from 'src/lib/portal-migration/portal-module-prefs'
import {readPortalModulePrefsFromClientCookie} from 'src/lib/portal-migration/portal-module-prefs.client'
import {useAppSidebar} from './app-sidebar-context'
import {AppSidebarNavIcon} from './app-sidebar-nav-icon'
import {AppSidebarSubnav} from './app-sidebar-subnav'

interface Props {
  navigationMenu: NavigationMenuType
  isOpen: boolean
  onOpenChange: (id: string, open: boolean) => void
  isHoverCardOpen: boolean
  onHoverCardOpenChange: (open: boolean) => void
}

const navItemVariants = tv({
  base: [
    'group flex w-full items-center gap-3 h-10',
    'rounded-xl',
    'px-3 py-2',
    'text-sm font-medium no-underline',
    'transition-colors',
    'min-w-0 overflow-hidden',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring hover:bg-[#182B4466]',
    'hover:rounded-[5px_0_0_5px]',
  ],
  variants: {
    active: {
      true: 'bg-sidebar-accent text-sidebar-foreground visited:text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground active:text-sidebar-foreground hover:rounded-[5px_0_0_5px] font-bold',
      false: 'text-sidebar-foreground visited:text-sidebar-foreground hover:text-sidebar-foreground active:text-sidebar-foreground font-normal',
    },
    collapsed: {
      true: 'justify-center',
      false: 'justify-between',
    },
  },
})

const HOVER_CARD_SIDE_OFFSET = 15

const hoverCardContentClassName = 'overflow-visible border-0 bg-transparent p-0 shadow-none outline-none'

const hoverCardPanelClassName = cn(
  'bg-sidebar relative z-[1] w-[242px] overflow-visible rounded-xl border border-sidebar-border/45 p-2 shadow-[0_18px_44px_rgba(8,13,24,0.26),0_2px_8px_rgba(8,13,24,0.16)] transition-all',
  'before:absolute before:left-[-6px] before:top-1/2 before:h-3 before:w-3 before:-translate-y-1/2 before:rotate-45 before:border-b before:border-l before:border-sidebar-border/45 before:bg-sidebar before:content-[""]',
)

const hoverCardSafeZoneClassName = 'pointer-events-auto absolute -bottom-4 -left-[30px] -right-4 -top-4 z-0 w-[calc(100%+2.875rem)]'

export function AppSidebarNavItem({navigationMenu, isOpen, onOpenChange, isHoverCardOpen, onHoverCardOpenChange}: Props) {
  const {collapsed, toggleCollapsed} = useAppSidebar()
  const {pathname} = useLocation()
  const navigate = useNavigate()
  const {width} = useWindowSize()
  const mobileWidth = width <= 767

  const active = useMemo(() => {
    const selfActive = isPathActive(pathname, navigationMenu.route || '')
    const childActive = navigationMenu.children?.some((child) => isPathActive(pathname, child.route || '')) ?? false
    return selfActive || childActive
  }, [pathname, navigationMenu])

  useEffect(() => {
    if (active && !collapsed) {
      onOpenChange(navigationMenu.id, true)
    }
  }, [active, collapsed, onOpenChange, navigationMenu.id, pathname])

  const hasChildren = Boolean(navigationMenu.children?.length)
  const firstChildRoute = navigationMenu.children?.[0]?.route

  const handleHoverCardOpenChange = (open: boolean) => {
    if (open && isOpen && !collapsed) return
    onHoverCardOpenChange(open)
  }

  const openCollapsedHoverCard = () => {
    if (collapsed && hasChildren) onHoverCardOpenChange(true)
  }

  const navItemClassName = cn(navItemVariants({active, collapsed}), collapsed && isHoverCardOpen ? 'rounded-[5px_0_0_5px]' : 'rounded-[5px]')

  const navItemContent = (
    <>
      <span className={cn('shrink-0', collapsed && 'flex  w-[18px] items-center justify-center')}>
        <AppSidebarNavIcon icon={navigationMenu.icon} active={active} />
      </span>

      {!collapsed && (
        <span className='flex min-w-0 flex-1 items-center justify-between overflow-hidden'>
          <span className='min-w-0 flex-1 truncate text-left'>{navigationMenu.label}</span>

          {hasChildren &&
            (isOpen ? (
              <ChevronDown className='text-sidebar-foreground size-4 shrink-0' />
            ) : (
              <ChevronRight className='text-sidebar-foreground size-4 shrink-0' />
            ))}
        </span>
      )}
    </>
  )

  return (
    <HoverCard open={isHoverCardOpen} onOpenChange={handleHoverCardOpenChange} openDelay={100} closeDelay={400}>
      <div className='py-0.5'>
        <Collapsible open={isOpen}>
          <div className='relative'>
            <HoverCardTrigger asChild>
              {hasChildren ? (
                <button
                  type='button'
                  data-slot='sidebar-item'
                  data-active={active ? active : undefined}
                  onPointerEnter={openCollapsedHoverCard}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    if (collapsed) {
                      onHoverCardOpenChange(true)
                      return
                    }
                    onHoverCardOpenChange(false)
                    const nextOpen = !isOpen
                    onOpenChange(navigationMenu.id, nextOpen)
                    if (nextOpen && firstChildRoute && !isPathActive(pathname, firstChildRoute)) {
                      const prefs = readPortalModulePrefsFromClientCookie()
                      if (isPortalRouteHybridEnabled() && isMigratedLegacyPath(firstChildRoute) && isNewPortalEnabledForPath(firstChildRoute, prefs)) {
                        navigateToMigratedRouteOnNewPortal(firstChildRoute)
                      } else {
                        navigate(firstChildRoute)
                      }
                    }
                    if (mobileWidth) toggleCollapsed()
                  }}
                  className={navItemClassName}>
                  {navItemContent}
                </button>
              ) : (
                <SidebarNavLink
                  route={navigationMenu.route || '#'}
                  data-slot='sidebar-item'
                  data-active={active ? active : undefined}
                  className={navItemClassName}
                  style={{textDecoration: 'none'}}
                  onNavigate={() => {
                    onHoverCardOpenChange(false)
                    if (mobileWidth) toggleCollapsed()
                  }}>
                  {navItemContent}
                </SidebarNavLink>
              )}
            </HoverCardTrigger>
          </div>
          {hasChildren && !collapsed && (
            <CollapsibleContent className='data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down overflow-hidden transition-all'>
              <AppSidebarSubnav
                items={
                  navigationMenu.children.map((child) => ({
                    id: child.id,
                    label: child.label,
                    route: child.route,
                    icon: child.icon,
                  })) || []
                }
              />
            </CollapsibleContent>
          )}
        </Collapsible>
      </div>

      {hasChildren && (
        <HoverCardContent
          side='right'
          align='center'
          sideOffset={HOVER_CARD_SIDE_OFFSET}
          className={hoverCardContentClassName}
          onPointerEnter={openCollapsedHoverCard}
          onEscapeKeyDown={() => onHoverCardOpenChange(false)}>
          <div className='relative'>
            <div aria-hidden className={hoverCardSafeZoneClassName} />
            <div className={hoverCardPanelClassName}>
              <div className='mx-1 mb-2 flex h-9 items-center gap-2.5 border-b border-[#26384E] px-3'>
                <span className='flex size-4 shrink-0 items-center justify-center text-white/85'>
                  <AppSidebarNavIcon icon={navigationMenu.icon} active={active} className='size-4' />
                </span>
                <h3 className='text-[13px] font-semibold tracking-[-0.01em] text-[#F6FAFF]'>{navigationMenu.label}</h3>
              </div>
              <AppSidebarSubnav
                variant='popover'
                items={navigationMenu.children.map((child) => ({
                  id: child.id,
                  label: child.label,
                  route: child.route,
                  icon: child.icon,
                }))}
              />
            </div>
          </div>
        </HoverCardContent>
      )}
    </HoverCard>
  )
}
